import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { trackSignup, trackEvent } from '../lib/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    console.log('AuthContext - checkAuth called');
    try {
      // Check localStorage for auth token
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        console.log('AuthContext - No token in localStorage, user not authenticated');
        setLoading(false);
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      console.log('AuthContext - Token found, verifying with server...');
      
      // Verify token with server
      try {
        const userData = await authAPI.getGoogleUser();
        console.log('AuthContext - User verified:', userData.email);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (e) {
        console.log('AuthContext - Token invalid or expired, clearing...');
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('AuthContext - Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (token) => {
    localStorage.setItem('auth_token', token);
    await checkAuth();
    // Detect new signup vs returning login by comparing account age.
    // A user whose account was created in the last 2 minutes is a new signup.
    try {
      const userData = await authAPI.getGoogleUser();
      if (userData?.created_at) {
        const ageSeconds = (Date.now() - new Date(userData.created_at).getTime()) / 1000;
        if (ageSeconds < 120) {
          trackSignup('google');
        } else {
          trackEvent('login', { method: 'google' });
        }
      } else {
        trackEvent('login', { method: 'google' });
      }
    } catch (e) {
      // Analytics should never break login
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('auth_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Google OAuth - redirect to Emergent Auth
  // Supports optional promo code for new user bonus credits
  const initiateGoogleLogin = (promoCode = null) => {
    authAPI.initiateGoogleLogin('/auth/callback', promoCode);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    initiateGoogleLogin,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
